'use client';

import {
    MediaViewErrorFrame,
    MediaViewInfoMenuFrame,
    MediaViewLoadingFrame,
    MediaViewMediaFrame
} from "@/app/features/shared/media-view/ui/shared/MediaViewFrames";
import {
    mediaRefCaption,
    mediaRefName,
    mediaRefType,
    mediaRefUrl
} from "@/app/features/shared/__to_be_integrated/types/general/media/media-helper";
import dynamic from "next/dynamic";
import { CSSProperties, KeyboardEvent, memo, MouseEvent, SyntheticEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import LoadingSpinner from "@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner";
import { Box, Stack, Typography } from "@mui/material";
import { Enum_Componentlinkslink_Type } from "@/app/lib/generated/graphql-types";
import LinkOptional from "@/app/features/shared/__to_be_integrated/ui/LinkOptional";
import Image from "next/image";
import DetailsMenu from "@/app/features/shared/media-view/ui/shared/DetailsMenu";
import type { ImageMeta, MediaViewProps } from "@/app/features/shared/media-view/types/types";
import MagnifierGlass from "@/app/features/shared/media-view/ui/shared/MagnifierGlass";
import { resolveObjectPositionOffsets } from "@/app/features/shared/media-view/helper/magnifier-helper";
import useIsDesktop from "@/hooks/useDesktop";
import { MagnifierLayout, PendingMagnifierPointer } from "@/app/features/shared/media-view/types/types";
import ImageModal from "@/app/features/shared/media-view/ui/shared/modal/ImageModal";

const ImageView = dynamic(() => import('../shared/media-views/ImageView'));
const VideoView = dynamic(() => import('../shared/media-views/VideoView'));
const PDFView = dynamic(() => import('../shared/media-views/PDFView'));
const ImageMetaOverlay = dynamic(() => import('@/app/features/shared/media-view/ui/shared/MetaOverlay'));

const MAGNIFIER_SIZE = 250;
const MAGNIFIER_ZOOM_LEVEL = 2.5;
const VIDEO_AND_PDF_LAZY_ROOT_MARGIN = '300px 0px';

enum MediaLoadState {
    LOADING = 'loading',
    LOADED = 'loaded',
    ERROR = 'error',
}

export default function MediaViewPublic({
    src,
    link,
    relatedImages,
    fill,
    slots,
    showLoadingSpinner = true,
    magnifier = 'none',
    modalViewType = 'none',
    infoViewType = 'none',
    metadataOverlayOptions,
    showDetailsMenu = false,
    detailsMenuPosition = 'top right',
    objectFit,
    objectPosition,
    ...props
}: MediaViewProps) {
    const isDesktop = useIsDesktop();
    const mediaType = useMemo(() => mediaRefType(src), [src]);
    const isImageMedia = mediaType === 'image';
    const isVideoOrPdf = mediaType === 'video' || mediaType === 'pdf';

    const [loadingState, setLoadingState] = useState<MediaLoadState>(MediaLoadState.LOADING);
    const [canStartMediaLoad, setCanStartMediaLoad] = useState<boolean>(() => !isVideoOrPdf);
    const [isMagnifying, setIsMagnifying] = useState(false);
    const [showMagnifierGlass, setShowMagnifierGlass] = useState(false);
    const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
    const [isModalViewOpen, setIsModalViewOpen] = useState(false);

    const viewRef = useRef<HTMLSpanElement | null>(null);
    const frameRef = useRef<number | null>(null);
    const pendingPointerRef = useRef<PendingMagnifierPointer | null>(null);
    const magnifierLayoutRef = useRef<MagnifierLayout | null>(null);
    const magnifierGlassRef = useRef<HTMLDivElement | null>(null);

    const magnifierEnabled = isImageMedia && (magnifier === 'image' || magnifier === 'both');
    const isMagnifierActive = magnifierEnabled && isMagnifying && loadingState === MediaLoadState.LOADED;
    const showImageInfoButton = infoViewType === 'image' || infoViewType === 'both';
    const modalViewByClick = isImageMedia && modalViewType === 'click';
    const modalViewByButton = isImageMedia && modalViewType === 'button';
    const modalMagnifierEnabled = isDesktop && isImageMedia && (magnifier === 'modal' || magnifier === 'both');
    const modalImageInfoEnabled = isDesktop && isImageMedia && (infoViewType === 'modal' || infoViewType === 'both');

    const imageMeta: ImageMeta | undefined = useMemo(() => {
        if (!isImageMedia) return undefined;
        return {
            name: mediaRefName(src),
            caption: mediaRefCaption(src),
        };
    }, [isImageMedia, src]);

    const isDetailsMenuVisible = useMemo(() => {
        if (!showDetailsMenu) return false;
        if (!isImageMedia || loadingState === MediaLoadState.ERROR) return false;
        const hasMeta = Boolean(imageMeta?.name || imageMeta?.caption);
        return magnifierEnabled || (showImageInfoButton && hasMeta) || modalViewByButton;
    }, [imageMeta?.caption, imageMeta?.name, isImageMedia, loadingState, magnifierEnabled, modalViewByButton, showDetailsMenu, showImageInfoButton]);

    const {
        className: rootSlotClassName,
        sx: rootSlotSx,
        ...rootSlotProps
    } = slots?.root ?? {};

    const {
        className: innerSlotClassName,
        sx: innerSlotSx,
        onClick: innerOnClick,
        onMouseLeave: innerOnMouseLeave,
        onMouseEnter: innerOnMouseEnter,
        onMouseMove: innerOnMouseMove,
        onKeyDown: innerOnKeyDown,
        ...innerSlotProps
    } = slots?.inner ?? {};

    const cancelQueuedFrame = useCallback(() => {
        if (frameRef.current === null || typeof window === 'undefined') return;
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
    }, []);

    const applyMagnifierStyles = useCallback((mouseX: number, mouseY: number, bgX: number, bgY: number) => {
        const magnifierGlassEl = magnifierGlassRef.current;
        if (!magnifierGlassEl) return;
        magnifierGlassEl.style.setProperty('--media-view-magnifier-glass-left', `${mouseX - (MAGNIFIER_SIZE / 2)}px`);
        magnifierGlassEl.style.setProperty('--media-view-magnifier-glass-top', `${mouseY - (MAGNIFIER_SIZE / 2)}px`);
        magnifierGlassEl.style.setProperty('--media-view-magnifier-bg-x', `${bgX}px`);
        magnifierGlassEl.style.setProperty('--media-view-magnifier-bg-y', `${bgY}px`);
    }, []);

    const measureMagnifierLayout = useCallback((containerEl: HTMLElement, containerRect?: DOMRect): MagnifierLayout | null => {
        const imgEl = containerEl.querySelector('img') as HTMLImageElement | null;
        if (!imgEl) return null;

        const nextContainerRect = containerRect ?? containerEl.getBoundingClientRect();
        const imgRect = imgEl.getBoundingClientRect();
        const computed = getComputedStyle(imgEl);

        let scaledW = imgRect.width || nextContainerRect.width;
        let scaledH = imgRect.height || nextContainerRect.height;
        let offsetX = 0;
        let offsetY = 0;

        if (imgEl.naturalWidth > 0 && imgEl.naturalHeight > 0) {
            const resolved = resolveObjectPositionOffsets(
                computed,
                nextContainerRect.width,
                nextContainerRect.height,
                imgEl.naturalWidth,
                imgEl.naturalHeight,
            );
            scaledW = resolved.scaledW;
            scaledH = resolved.scaledH;
            offsetX = resolved.offsetX;
            offsetY = resolved.offsetY;
        }

        const nextLayout = {
            containerWidth: nextContainerRect.width,
            containerHeight: nextContainerRect.height,
            scaledW,
            scaledH,
            offsetX,
            offsetY,
        };

        magnifierLayoutRef.current = nextLayout;
        setImageSize((previous) => {
            if (previous.width === scaledW && previous.height === scaledH) return previous;
            return { width: scaledW, height: scaledH };
        });

        return nextLayout;
    }, []);

    const updateMagnifierPosition = useCallback((containerEl: HTMLElement, clientX: number, clientY: number) => {
        const containerRect = containerEl.getBoundingClientRect();
        const hasSizeChanged = !magnifierLayoutRef.current
            || Math.abs(magnifierLayoutRef.current.containerWidth - containerRect.width) > 0.5
            || Math.abs(magnifierLayoutRef.current.containerHeight - containerRect.height) > 0.5;

        const layout = hasSizeChanged
            ? measureMagnifierLayout(containerEl, containerRect)
            : magnifierLayoutRef.current;

        if (!layout) return;

        const mouseXInContainer = clientX - containerRect.left;
        const mouseYInContainer = clientY - containerRect.top;

        const relXInScaledImage = mouseXInContainer + layout.offsetX;
        const relYInScaledImage = mouseYInContainer + layout.offsetY;

        applyMagnifierStyles(
            mouseXInContainer,
            mouseYInContainer,
            -relXInScaledImage * MAGNIFIER_ZOOM_LEVEL + (MAGNIFIER_SIZE / 2),
            -relYInScaledImage * MAGNIFIER_ZOOM_LEVEL + (MAGNIFIER_SIZE / 2),
        );
    }, [applyMagnifierStyles, measureMagnifierLayout]);

    const flushQueuedMagnifierUpdate = useCallback(() => {
        frameRef.current = null;
        const pendingPointer = pendingPointerRef.current;
        if (!pendingPointer) return;
        updateMagnifierPosition(pendingPointer.containerEl, pendingPointer.clientX, pendingPointer.clientY);
    }, [updateMagnifierPosition]);

    const queueMagnifierUpdate = useCallback((containerEl: HTMLElement, clientX: number, clientY: number) => {
        pendingPointerRef.current = { containerEl, clientX, clientY };
        if (typeof window === 'undefined') return;
        if (frameRef.current !== null) return;
        frameRef.current = window.requestAnimationFrame(flushQueuedMagnifierUpdate);
    }, [flushQueuedMagnifierUpdate]);

    useEffect(() => {
        return () => {
            cancelQueuedFrame();
            pendingPointerRef.current = null;
        };
    }, [cancelQueuedFrame]);

    useEffect(() => {
        setCanStartMediaLoad(!isVideoOrPdf);
    }, [isVideoOrPdf, src]);

    useEffect(() => {
        if (!isVideoOrPdf || canStartMediaLoad) return;
        const viewEl = viewRef.current;
        if (!viewEl || typeof IntersectionObserver === 'undefined') {
            setCanStartMediaLoad(true);
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setCanStartMediaLoad(true);
                observer.disconnect();
            }
        }, { rootMargin: VIDEO_AND_PDF_LAZY_ROOT_MARGIN });

        observer.observe(viewEl);
        return () => observer.disconnect();
    }, [canStartMediaLoad, isVideoOrPdf]);

    useEffect(() => {
        setLoadingState(MediaLoadState.LOADING);
        setIsMagnifying(false);
        setShowMagnifierGlass(false);
        setIsModalViewOpen(false);
        setImageSize({ width: 0, height: 0 });
        magnifierLayoutRef.current = null;
        pendingPointerRef.current = null;
        cancelQueuedFrame();
    }, [cancelQueuedFrame, mediaType, src]);

    useEffect(() => {
        if (magnifierEnabled) return;
        setIsMagnifying(false);
        setShowMagnifierGlass(false);
        cancelQueuedFrame();
        magnifierLayoutRef.current = null;
        pendingPointerRef.current = null;
    }, [cancelQueuedFrame, magnifierEnabled]);

    const handleMouseEnter = useCallback((event: MouseEvent<HTMLSpanElement>) => {
        if (!isMagnifierActive) return;
        measureMagnifierLayout(event.currentTarget);
        setShowMagnifierGlass(true);
        queueMagnifierUpdate(event.currentTarget, event.clientX, event.clientY);
    }, [isMagnifierActive, measureMagnifierLayout, queueMagnifierUpdate]);

    const handleMouseLeave = useCallback(() => {
        setShowMagnifierGlass(false);
        pendingPointerRef.current = null;
        cancelQueuedFrame();
    }, [cancelQueuedFrame]);

    const handleMouseMove = useCallback((event: MouseEvent<HTMLSpanElement>) => {
        if (!isMagnifierActive) return;
        if (!showMagnifierGlass) {
            setShowMagnifierGlass(true);
        }
        queueMagnifierUpdate(event.currentTarget, event.clientX, event.clientY);
    }, [isMagnifierActive, queueMagnifierUpdate, showMagnifierGlass]);

    const onMediaLoaded = useCallback(() => {
        setLoadingState(MediaLoadState.LOADED);
    }, []);

    const onMediaLoadError = useCallback(() => {
        setLoadingState(MediaLoadState.ERROR);
        setIsMagnifying(false);
        setShowMagnifierGlass(false);
        cancelQueuedFrame();
        pendingPointerRef.current = null;
    }, [cancelQueuedFrame]);

    const onMagnifierError = useCallback(() => {
        setIsMagnifying(false);
        setShowMagnifierGlass(false);
    }, []);

    const onSetMagnifying = useCallback((nextIsMagnifying: boolean) => {
        if (nextIsMagnifying && !magnifierEnabled) return;
        setIsMagnifying(nextIsMagnifying);
        if (!nextIsMagnifying) {
            setShowMagnifierGlass(false);
            pendingPointerRef.current = null;
            cancelQueuedFrame();
        }
    }, [cancelQueuedFrame, magnifierEnabled]);

    const onOpenModalView = useCallback(() => {
        if (!isImageMedia || modalViewType === 'none') return;
        setIsModalViewOpen(true);
    }, [isImageMedia, modalViewType]);

    const onCloseModalView = useCallback(() => {
        setIsModalViewOpen(false);
    }, []);

    const onInnerClickWithSlot = useCallback((event: MouseEvent<HTMLSpanElement>) => {
        (innerOnClick as ((event: MouseEvent<HTMLSpanElement>) => void) | undefined)?.(event);
        if (event.defaultPrevented) return;
        if (modalViewByClick) {
            onOpenModalView();
        }
    }, [innerOnClick, modalViewByClick, onOpenModalView]);

    const onInnerKeyDownWithSlot = useCallback((event: KeyboardEvent<HTMLSpanElement>) => {
        (innerOnKeyDown as ((event: KeyboardEvent<HTMLSpanElement>) => void) | undefined)?.(event);
        if (event.defaultPrevented) return;
        if (!modalViewByClick || link) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        onOpenModalView();
    }, [innerOnKeyDown, link, modalViewByClick, onOpenModalView]);

    const onInnerMouseLeaveWithSlot = useCallback((event: MouseEvent<HTMLSpanElement>) => {
        (innerOnMouseLeave as ((event: MouseEvent<HTMLSpanElement>) => void) | undefined)?.(event);
        if (!magnifierEnabled) return;
        handleMouseLeave();
    }, [handleMouseLeave, innerOnMouseLeave, magnifierEnabled]);

    const onInnerMouseEnterWithSlot = useCallback((event: MouseEvent<HTMLSpanElement>) => {
        (innerOnMouseEnter as ((event: MouseEvent<HTMLSpanElement>) => void) | undefined)?.(event);
        if (!magnifierEnabled) return;
        handleMouseEnter(event);
    }, [handleMouseEnter, innerOnMouseEnter, magnifierEnabled]);

    const onInnerMouseMoveWithSlot = useCallback((event: MouseEvent<HTMLSpanElement>) => {
        (innerOnMouseMove as ((event: MouseEvent<HTMLSpanElement>) => void) | undefined)?.(event);
        if (!magnifierEnabled) return;
        handleMouseMove(event);
    }, [handleMouseMove, innerOnMouseMove, magnifierEnabled]);

    const shouldShowLoadingFrame = loadingState === MediaLoadState.LOADING
        && (showLoadingSpinner || isVideoOrPdf);
    const isClickModalTrigger = modalViewByClick && !link;
    const clickModalTriggerLabel = imageMeta?.name
        ? `Bild "${imageMeta.name}" vergrößert öffnen`
        : 'Bild vergrößert öffnen';

    return (
        <Box
            ref={viewRef}
            component="span"
            className={[
                "relative flex flex-auto size-full",
                `media-view-${mediaType}`,
                rootSlotClassName
            ].filter(Boolean).join(' ')}
            sx={[
                {
                    position: 'relative',
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    flex: '1 1 auto',
                    minWidth: 0,
                    minHeight: 0,
                    maxWidth: '100%',
                    maxHeight: '100%',
                    ...(isImageMedia && fill ? { width: '100%', height: '100%' } : {}),
                },
                ...(Array.isArray(rootSlotSx) ? rootSlotSx : rootSlotSx ? [rootSlotSx] : [])
            ]}
            {...rootSlotProps}
        >
            <MediaViewMediaFrame isVisible={loadingState === MediaLoadState.LOADED}>
                <Box
                    component="span"
                    className={[
                        "relative flex flex-auto size-full",
                        `media-view-${mediaType}-inner`,
                        innerSlotClassName
                    ].filter(Boolean).join(' ')}
                    onClick={onInnerClickWithSlot}
                    onMouseLeave={onInnerMouseLeaveWithSlot}
                    onMouseEnter={onInnerMouseEnterWithSlot}
                    onMouseMove={onInnerMouseMoveWithSlot}
                    onKeyDown={onInnerKeyDownWithSlot}
                    role={isClickModalTrigger ? "button" : undefined}
                    tabIndex={isClickModalTrigger ? 0 : undefined}
                    aria-label={isClickModalTrigger ? clickModalTriggerLabel : undefined}
                    aria-haspopup={isClickModalTrigger ? "dialog" : undefined}
                    sx={[
                        {
                            position: 'relative',
                            ...(fill
                                ? {
                                    width: '100%',
                                    height: '100%',
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                }
                                : {
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                }),
                            justifyContent: 'center',
                            zIndex: 100,
                        },
                        ...(Array.isArray(innerSlotSx) ? innerSlotSx : innerSlotSx ? [innerSlotSx] : [])
                    ]}
                    {...innerSlotProps}
                >
                    <LinkOptional
                        enabled={!!link}
                        href={link?.url}
                        target={link?.type === Enum_Componentlinkslink_Type.External ? '_blank' : '_self'}
                        rel={link?.type === Enum_Componentlinkslink_Type.External ? 'noopener noreferrer' : undefined}
                    >
                        {canStartMediaLoad && (
                            <Content
                                {...props}
                                src={src}
                                fill={fill}
                                slots={slots}
                                link={link}
                                modalViewType={modalViewType}
                                objectFit={objectFit}
                                objectPosition={objectPosition}
                                onLoad={onMediaLoaded}
                                onError={onMediaLoadError}
                            />
                        )}
                    </LinkOptional>
                    {isMagnifierActive && showMagnifierGlass && (
                        <MagnifierGlass
                            glassRef={magnifierGlassRef}
                            image={src}
                            imageSize={imageSize}
                            zoomLevel={MAGNIFIER_ZOOM_LEVEL}
                            size={MAGNIFIER_SIZE}
                            onError={onMagnifierError}
                        />
                    )}
                </Box>
            </MediaViewMediaFrame>

            {shouldShowLoadingFrame && (
                <MediaViewLoadingFrame isVisible>
                    <LoadingSpinner />
                </MediaViewLoadingFrame>
            )}

            {loadingState === MediaLoadState.ERROR && (
                <MediaViewErrorFrame isVisible>
                    <ErrorView />
                </MediaViewErrorFrame>
            )}

            {isDetailsMenuVisible && (
                <MediaViewInfoMenuFrame>
                    <DetailsMenu
                        imageMeta={showImageInfoButton ? imageMeta : undefined}
                        position={detailsMenuPosition}
                        magnifierEnabled={magnifierEnabled}
                        isMagnifying={isMagnifierActive}
                        setIsMagnifying={onSetMagnifying}
                        onOpenInFull={modalViewByButton ? onOpenModalView : undefined}
                    />
                </MediaViewInfoMenuFrame>
            )}

            {isImageMedia && metadataOverlayOptions && loadingState === MediaLoadState.LOADED && (
                <ImageMetaOverlay
                    src={src}
                    options={metadataOverlayOptions}
                />
            )}

            {isModalViewOpen && (
                <ImageModal
                    isOpen={isModalViewOpen}
                    onClose={onCloseModalView}
                    image={src}
                    relatedImages={relatedImages}
                    magnifierEnabled={modalMagnifierEnabled}
                    imageInfoEnabled={modalImageInfoEnabled}
                />
            )}
        </Box>
    );
}

const Content = memo(function Content({
    onLoad,
    onError,
    src,
    alt,
    slots,
    objectFit,
    objectPosition,
    width,
    height,
    fill,
    zoomOnHover,
    link,
    modalViewType,
}: MediaViewProps & { onLoad: () => void; onError: () => void }) {
    const mediaType = mediaRefType(src);
    const mediaUrl = mediaRefUrl(src);
    const imageWidth = width ?? slots?.image?.width;
    const imageHeight = height ?? slots?.image?.height;
    const imageFill = fill ?? slots?.image?.fill;
    const imageObjectFit = objectFit ?? slots?.image?.style?.objectFit;
    const imageObjectPosition = objectPosition ?? slots?.image?.style?.objectPosition;

    const imageSizingProps = imageWidth == null && imageHeight == null && imageFill == null
        ? { fill: true as const }
        : { width: imageWidth, height: imageHeight, fill: imageFill };
    const imageStyle = imageObjectFit == null && imageObjectPosition == null
        ? slots?.image?.style
        : {
            ...(slots?.image?.style || {}),
            objectFit: imageObjectFit,
            objectPosition: imageObjectPosition,
        };
    const imageCursor = (modalViewType === 'click' || Boolean(link))
        ? 'pointer'
        : imageStyle?.cursor;

    function onLoadImage(event: SyntheticEvent<HTMLImageElement, Event>) {
        onLoad();
        slots?.image?.onLoad?.(event);
    }

    function onLoadImageError(event: SyntheticEvent<HTMLImageElement, Event>) {
        onError();
        slots?.image?.onError?.(event);
    }

    function onLoadVideo(event: SyntheticEvent<HTMLVideoElement, Event>) {
        onLoad();
        slots?.video?.onLoadedData?.(event);
    }

    function onLoadVideoError(event: SyntheticEvent<HTMLVideoElement, Event>) {
        onError();
        slots?.video?.onError?.(event);
    }

    function onLoadPDF() {
        onLoad();
        slots?.pdf?.onLoad?.();
    }

    function onLoadPDFError(error: Error) {
        onError();
        slots?.pdf?.onError?.(error);
    }

    switch (mediaType) {
        case 'image':
            return (
                <ImageView
                    {...slots?.image}
                    src={src}
                    alt={alt}
                    zoomOnHover={zoomOnHover}
                    {...imageSizingProps}
                    style={{
                        ...(imageStyle || {}),
                        cursor: imageCursor,
                    }}
                    onLoad={onLoadImage}
                    onError={onLoadImageError}
                />
            );
        case 'video':
            return (
                <VideoView
                    {...slots?.video}
                    src={src}
                    onLoadedData={onLoadVideo}
                    onError={onLoadVideoError}
                    style={{
                        objectFit: objectFit ?? (slots?.video?.style?.objectFit as CSSProperties['objectFit']) ?? 'contain',
                        objectPosition: objectPosition ?? (slots?.video?.style?.objectPosition as CSSProperties['objectPosition']),
                        ...(slots?.video?.style || {})
                    }}
                />
            );
        case 'pdf':
            return (
                <PDFView
                    {...slots?.pdf}
                    src={mediaUrl}
                    width={imageWidth ?? slots?.pdf?.width}
                    height={imageHeight ?? slots?.pdf?.height}
                    onLoad={onLoadPDF}
                    onError={onLoadPDFError}
                />
            );
        default:
            return <div>Unbekannter Medientyp: {mediaType}</div>;
    }
});

function ErrorView() {
    return (
        <Stack spacing={1} alignItems="center" justifyContent="center">
            <Image src="/assets/placeholder/media-error-error.svg" alt="Fehler beim Laden der Medienansicht" width={150} height={100} />
            <Typography color="error" variant="overline">Fehler beim Laden</Typography>
        </Stack>
    );
}
